import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';
import { paths } from '../../../routing/path';
import { QueryParams } from '../../../utils/queryParams';
import { useStateSelector } from '../../../store/selectors';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { SellerMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/seller-marketplace-tab.enum';
import { BuyerOppMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/buyer-opportunity-details-tab.enum';
import { MarketplaceCommsHubTab } from '../../AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHubTabs';
import { CreateClosingDateDiscussion } from '../../../slices/marketplace/closing-date/thunks/create-closing-date-discussion.thunk';
import PortalTooltip from '../../../components/PortalTooltip/PortalTooltip';

const SCHEDULING_TOOLTIP =
    'This is to take you to a discussion thread dedicated to agreeing on a closing date';

export interface ClosingDateSchedulingBlockProps {
    engagementId: number;
    threadedDiscussionId?: number | null;
    commsId?: number | null;
    currentUserParty?: TimelineParty | null;
    isEngagementClosed: boolean;
}

export const ClosingDateSchedulingBlock = ({
    engagementId,
    threadedDiscussionId,
    commsId,
    currentUserParty,
    isEngagementClosed,
}: ClosingDateSchedulingBlockProps) => {
    const dispatch = useDispatch() as any;
    const navigate = useNavigate();
    const party = useCurrentMarketplaceParty(engagementId);
    const isBuyer = party === TimelineParty.Buyer;
    const isSubmitting = useStateSelector(
        (state) => state.closingDateState.isSubmitting
    );
    const isDiscussionCreated = !!threadedDiscussionId && !!commsId;
    const canCreateDiscussion = !!currentUserParty && !isEngagementClosed;
    const isInteractive = isDiscussionCreated || canCreateDiscussion;

    const navigateToDiscussion = (
        discussionCommsId: number,
        discussionId: number
    ) => {
        const commsHubTab = isBuyer
            ? BuyerOppMarketplaceTab.CommsHub
            : SellerMarketplaceTab.CommsHub;

        if (isBuyer) {
            navigate(
                `${paths.venues.connect.opportunities}?opportunityId=${engagementId}&tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${discussionCommsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        } else {
            navigate(
                `${paths.venues.connect.engagement(engagementId)}?tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${discussionCommsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        }
    };

    const handleClick = async () => {
        if (isSubmitting || !isInteractive) {
            return;
        }
        if (isDiscussionCreated) {
            navigateToDiscussion(commsId as number, threadedDiscussionId as number);
            return;
        }
        try {
            const result = await dispatch(
                CreateClosingDateDiscussion(engagementId)
            ).unwrap();
            if (result.threadedDiscussionId && result.commsId) {
                navigateToDiscussion(
                    result.commsId,
                    result.threadedDiscussionId
                );
            }
        } catch {
            return;
        }
    };

    const anchorId = `closing-date-scheduling-${engagementId}`;

    return (
        <>
            <span id={anchorId} className="closing-date-scheduling">
                <button
                    type="button"
                    className="closing-date-scheduling-button"
                    aria-label={SCHEDULING_TOOLTIP}
                    disabled={!isInteractive || isSubmitting}
                    onClick={handleClick}>
                    <FontAwesomeIcon icon={faComments} />
                </button>
            </span>
            <PortalTooltip
                anchorId={anchorId}
                delayShow={0}
                content={SCHEDULING_TOOLTIP}
            />
        </>
    );
};
